import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    Paper,
    Popover,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    deleteDeferredAction,
    getAllDeferredActions,
    getDeferredActionRunLogsErrorTypeTrend
} from "../api/AiAgentApi";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const TREND_SERIES = [
    "SUCCESS",
    "TELEGRAM_BLOCKED_BY_USER",
    "TELEGRAM_CHAT_NOT_FOUND",
    "USER_DISABLED_MAILING",
    "UNKNOWN"
];
const TREND_SERIES_META = {
    SUCCESS: {label: "Успешно", color: "#2e7d32"},
    TELEGRAM_BLOCKED_BY_USER: {label: "Пользователь заблокировал бота", color: "#ed6c02"},
    TELEGRAM_CHAT_NOT_FOUND: {label: "Чат не найден", color: "#d32f2f"},
    USER_DISABLED_MAILING: {label: "Пользователь отключил рассылку", color: "#0288d1"},
    UNKNOWN: {label: "Неизвестная ошибка", color: "#6b7280"},
};
const RUN_LOG_TRIGGER_TYPE_LABELS = {
    MANUAL: "Ручной запуск",
    SCHEDULED: "По расписанию",
    DO_LATER: "Отложенный запуск",
};
const RUN_LOG_RUN_TYPE_LABELS = {
    ACTION_ALL_USERS: "Для всех пользователей",
    ACTION_SINGLE_USER: "Для одного пользователя",
    ALL_ENABLED_ACTIONS: "Все включенные действия",
};
const ACTION_TYPE_FILTERS = [
    {value: "check", label: "check"},
    {value: "send_message_telegram", label: "send_message_telegram"},
    {value: "send_message_vk", label: "send_message_vk"},
];

function ErrorTypeTrendChart({points, onNavigateToAction}) {
    const minWidth = 980;
    const pointStep = 110;
    const width = Math.max(minWidth, (points.length - 1) * pointStep + 160);
    const height = 340;
    const margin = {top: 20, right: 20, bottom: 72, left: 48};
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const xInnerPadding = 26;
    const pointCount = points.length;
    const [popup, setPopup] = useState(null);
    const popupOpen = Boolean(popup?.anchorEl);
    const [visibleTypes, setVisibleTypes] = useState(() =>
        Object.fromEntries(TREND_SERIES.map((type) => [type, true]))
    );
    const activeTypes = TREND_SERIES.filter((type) => visibleTypes[type]);
    const getSeriesValue = (point, type) => {
        if (type === "SUCCESS") return Number(point?.successCount) || 0;
        return Number(point?.errorTypeCounts?.[type]) || 0;
    };

    const maxY = Math.max(
        1,
        ...points.flatMap((p) =>
            (activeTypes.length > 0 ? activeTypes : TREND_SERIES).map((type) => getSeriesValue(p, type))
        )
    );
    const runCountByAction = useMemo(() => {
        const acc = {};
        for (const p of points) {
            const key = p?.deferredActionId;
            if (key == null) continue;
            acc[key] = (acc[key] || 0) + 1;
        }
        return acc;
    }, [points]);

    const xAt = (index) => {
        const usableWidth = Math.max(1, plotWidth - xInnerPadding * 2);
        if (pointCount <= 1) return margin.left + xInnerPadding + usableWidth / 2;
        return margin.left + xInnerPadding + (index / (pointCount - 1)) * usableWidth;
    };
    const yAt = (value) => margin.top + plotHeight - (Math.max(0, Math.min(value, maxY)) / maxY) * plotHeight;
    const fmtShortMsk = (iso) => {
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return "—";
            return new Intl.DateTimeFormat("ru-RU", {
                timeZone: "Europe/Moscow",
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }).format(d).replace(".", "");
        } catch {
            return "—";
        }
    };
    const fmtFullMsk = (iso) => {
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return "—";
            return new Intl.DateTimeFormat("ru-RU", {
                timeZone: "Europe/Moscow",
                year: "numeric",
                month: "long",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }).format(d);
        } catch {
            return "—";
        }
    };
    const fmtUtc = (iso) => {
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return "—";
            return new Intl.DateTimeFormat("ru-RU", {
                timeZone: "UTC",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }).format(d);
        } catch {
            return "—";
        }
    };
    const truncateLabel = (value, max = 28) => {
        const text = String(value || "");
        if (text.length <= max) return text;
        return text.slice(0, max - 1) + "…";
    };
    const xLabel = (point) => {
        const actionName = point?.deferredActionName || `Action #${point?.deferredActionId ?? "—"}`;
        const actionRuns = runCountByAction[point?.deferredActionId] || 0;
        if (actionRuns <= 1) return actionName;
        return `${actionName} • ${fmtShortMsk(point?.runCreatedAt)}`;
    };
    const openPointPopup = (event, point, type, value) => {
        setPopup({
            anchorEl: event.currentTarget,
            point,
            type,
            value,
        });
    };
    const closePopup = () => setPopup(null);
    const handleNavigateFromPopup = () => {
        const deferredActionId = popup?.point?.deferredActionId;
        if (deferredActionId == null) return;
        closePopup();
        onNavigateToAction?.(deferredActionId);
    };
    const toggleTypeVisibility = (type) => {
        setVisibleTypes((prev) => ({...prev, [type]: !prev[type]}));
    };
    const getTriggerTypeLabel = (value) =>
        RUN_LOG_TRIGGER_TYPE_LABELS[value] || value || "—";
    const getRunTypeLabel = (value) =>
        RUN_LOG_RUN_TYPE_LABELS[value] || value || "—";
    const getSeriesLabel = (value) =>
        TREND_SERIES_META[value]?.label || value || "—";
    const buildSmoothPath = (coords) => {
        if (!coords || coords.length === 0) return "";
        if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;

        const tension = 1;
        let d = `M ${coords[0].x} ${coords[0].y}`;

        for (let i = 0; i < coords.length - 1; i++) {
            const p0 = i > 0 ? coords[i - 1] : coords[i];
            const p1 = coords[i];
            const p2 = coords[i + 1];
            const p3 = i !== coords.length - 2 ? coords[i + 2] : p2;

            const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension;
            const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension;
            const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension;
            const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension;

            d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
        }
        return d;
    };
    useEffect(() => {
        if (popup?.type && !visibleTypes[popup.type]) {
            setPopup(null);
        }
    }, [popup, visibleTypes]);

    const yTicks = Array.from(
        new Set([0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxY * ratio)).sort((a, b) => a - b))
    );
    const xTickCount = pointCount <= 12 ? pointCount : 10;
    const xTickIndices = Array.from(
        new Set(
            Array.from({length: xTickCount}, (_, i) =>
                Math.round((i * Math.max(0, pointCount - 1)) / Math.max(1, xTickCount - 1))
            )
        )
    );

    const seriesCoords = activeTypes.reduce((acc, type) => {
        acc[type] = points.map((p, i) => ({
            x: xAt(i),
            y: yAt(getSeriesValue(p, type)),
            value: getSeriesValue(p, type),
            runLogId: p?.runLogId,
            runCreatedAt: p?.runCreatedAt,
        }));
        return acc;
    }, {});

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{mb: 1}}>
                {TREND_SERIES.map((type) => (
                    <Chip
                        key={type}
                        size="small"
                        variant="outlined"
                        label={`${visibleTypes[type] ? "✓" : "○"} ${TREND_SERIES_META[type].label}`}
                        onClick={() => toggleTypeVisibility(type)}
                        sx={{
                            cursor: "pointer",
                            borderColor: TREND_SERIES_META[type].color,
                            color: TREND_SERIES_META[type].color,
                            bgcolor: visibleTypes[type] ? `${TREND_SERIES_META[type].color}20` : "transparent",
                            opacity: visibleTypes[type] ? 1 : 0.5,
                        }}
                    />
                ))}
                <Typography variant="caption" color="text.secondary">
                    Нажмите на линию, чтобы скрыть или показать ее.
                </Typography>
            </Stack>

            <Box sx={{width: "100%", overflowX: "auto", pb: 0.5}}>
                <svg width={width} height={height} role="img" aria-label="График тренда запусков">
                    {yTicks.map((tick) => {
                        const y = yAt(tick);
                        return (
                            <g key={`y-${tick}`}>
                                <line
                                    x1={margin.left}
                                    x2={margin.left + plotWidth}
                                    y1={y}
                                    y2={y}
                                    stroke="rgba(255,255,255,0.12)"
                                    strokeDasharray="3 3"
                                />
                                <text x={margin.left - 8} y={y + 4} textAnchor="end" fontSize="11" fill="#8a8a8a">
                                    {tick}
                                </text>
                            </g>
                        );
                    })}

                    <line
                        x1={margin.left}
                        x2={margin.left}
                        y1={margin.top}
                        y2={margin.top + plotHeight}
                        stroke="rgba(255,255,255,0.25)"
                    />
                    <line
                        x1={margin.left}
                        x2={margin.left + plotWidth}
                        y1={margin.top + plotHeight}
                        y2={margin.top + plotHeight}
                        stroke="rgba(255,255,255,0.25)"
                    />

                    {activeTypes.map((type) => {
                        const coords = seriesCoords[type];
                        const d = buildSmoothPath(coords);
                        return (
                            <g key={type}>
                                <path
                                    d={d}
                                    fill="none"
                                    stroke={TREND_SERIES_META[type].color}
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {coords.map((c, i) => (
                                    <circle
                                        key={`${type}-${i}`}
                                        cx={c.x}
                                        cy={c.y}
                                        r="3.5"
                                        fill={TREND_SERIES_META[type].color}
                                        style={{cursor: "pointer"}}
                                        onClick={(event) => openPointPopup(event, points[i], type, c.value)}
                                    >
                                        <title>{`${getSeriesLabel(type)}: ${c.value}\nrunLogId: ${c.runLogId}\n${c.runCreatedAt}`}</title>
                                    </circle>
                                ))}
                            </g>
                        );
                    })}

                    {activeTypes.length === 0 && (
                        <text
                            x={margin.left + plotWidth / 2}
                            y={margin.top + plotHeight / 2}
                            textAnchor="middle"
                            fontSize="14"
                            fill="#8a8a8a"
                        >
                            Все линии скрыты. Включите хотя бы одну линию.
                        </text>
                    )}

                    {xTickIndices.map((index) => {
                        const x = xAt(index);
                        const point = points[index];
                        const fullLabel = xLabel(point);
                        return (
                            <g key={`x-${index}`}>
                                <line
                                    x1={x}
                                    x2={x}
                                    y1={margin.top + plotHeight}
                                    y2={margin.top + plotHeight + 6}
                                    stroke="rgba(255,255,255,0.25)"
                                />
                                <text
                                    x={x - 2}
                                    y={margin.top + plotHeight + 18}
                                    textAnchor="end"
                                    fontSize="11"
                                    fill="#8a8a8a"
                                    transform={`rotate(-24 ${x - 2} ${margin.top + plotHeight + 18})`}
                                >
                                    {truncateLabel(fullLabel)}
                                    <title>{fullLabel}</title>
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </Box>

            <Stack direction="row" justifyContent="space-between" sx={{mt: 0.5}}>
                <Typography variant="caption" color="text.secondary">Y: Количество событий</Typography>
                <Typography variant="caption" color="text.secondary">X: Время запуска (МСК)</Typography>
            </Stack>

            <Popover
                open={popupOpen}
                anchorEl={popup?.anchorEl || null}
                onClose={closePopup}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                transformOrigin={{vertical: "top", horizontal: "center"}}
                PaperProps={{sx: {p: 1.5, maxWidth: 420}}}
            >
                <Stack spacing={0.75}>
                    <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                        {popup?.point?.deferredActionName || "Без названия"} (ID: {popup?.point?.deferredActionId ?? "—"})
                    </Typography>
                    <Typography variant="body2">runLogId: {popup?.point?.runLogId ?? "—"}</Typography>
                    <Typography variant="body2">Триггер запуска: {getTriggerTypeLabel(popup?.point?.runLogTriggerType)}</Typography>
                    <Typography variant="body2">Тип запуска: {getRunTypeLabel(popup?.point?.runLogRunType)}</Typography>
                    <Typography variant="body2">
                        Время запуска (МСК): {fmtFullMsk(popup?.point?.runCreatedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Время запуска (UTC): {fmtUtc(popup?.point?.runCreatedAt)}
                    </Typography>
                    <Typography variant="body2" sx={{fontWeight: 600}}>
                        {getSeriesLabel(popup?.type)}: {popup?.value ?? 0}
                    </Typography>

                    <Divider />
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {TREND_SERIES.map((type) => (
                            <Chip
                                key={type}
                                size="small"
                                variant="outlined"
                                label={`${getSeriesLabel(type)}: ${getSeriesValue(popup?.point, type)}`}
                                sx={{borderColor: TREND_SERIES_META[type].color, color: TREND_SERIES_META[type].color}}
                            />
                        ))}
                    </Stack>

                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleNavigateFromPopup}
                        disabled={popup?.point?.deferredActionId == null}
                        sx={{alignSelf: "flex-start", textTransform: "none", mt: 0.5}}
                    >
                        Перейти к действию
                    </Button>
                </Stack>
            </Popover>
        </Box>
    );
}

export const DeferredActionPage = () => {
    const navigate = useNavigate();

    const OVERALL_STATUS = {
        NOT_STARTED: { label: "Не запущен", color: "default", variant: "outlined" },
        IN_PROGRESS: { label: "В процессе", color: "warning", variant: "filled" },
    };

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [trendLoading, setTrendLoading] = useState(true);
    const [trendErr, setTrendErr] = useState(null);
    const [trendPoints, setTrendPoints] = useState([]);
    const [includedActionTypes, setIncludedActionTypes] = useState(["send_message_telegram"]);
    const [excludeMixedActionTypes, setExcludeMixedActionTypes] = useState(false);

    const handleCreateDeferredAction = () => navigate("/create-deferred-action");

    const fetchActionsData = async () => {
        try {
            setLoading(true);
            setErr(null);
            const data = await getAllDeferredActions();
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e?.message || "Не удалось загрузить данные");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrendData = async (
        actionTypes = includedActionTypes,
        excludeMixed = excludeMixedActionTypes
    ) => {
        try {
            setTrendLoading(true);
            setTrendErr(null);
            const data = await getDeferredActionRunLogsErrorTypeTrend(actionTypes, excludeMixed);
            setTrendPoints(Array.isArray(data) ? data : []);
        } catch (e) {
            setTrendErr(e?.message || "Не удалось загрузить тренд ошибок");
            setTrendPoints([]);
        } finally {
            setTrendLoading(false);
        }
    };
    const handleRefresh = async () => {
        await Promise.all([
            fetchActionsData(),
            fetchTrendData(includedActionTypes, excludeMixedActionTypes),
        ]);
    };
    const toggleActionTypeFilter = (type) => {
        setIncludedActionTypes((prev) =>
            prev.includes(type) ? prev.filter((v) => v !== type) : [...prev, type]
        );
    };

    useEffect(() => {
        fetchActionsData();
    }, []);
    useEffect(() => {
        fetchTrendData(includedActionTypes, excludeMixedActionTypes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includedActionTypes, excludeMixedActionTypes]);

    const rows = useMemo(
        () =>
            items
                ?.slice()
                .sort((a, b) => (b.createdAt ?? "").localeCompare?.(a.createdAt ?? "")),
        [items]
    );

    const fmt = (iso) => {
        try {
            const d = new Date(iso);
            return isNaN(d.getTime()) ? String(iso ?? "") : d.toLocaleString("ru-RU");
        } catch {
            return String(iso ?? "");
        }
    };

    const fmtMsk = (iso) => {
        try {
            const d = new Date(iso);
            if (isNaN(d.getTime())) return String(iso ?? "");
            return `${new Intl.DateTimeFormat("ru-RU", {
                timeZone: "Europe/Moscow",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }).format(d)} МСК`;
        } catch {
            return String(iso ?? "");
        }
    };

    const renderNextLaunch = (row) => {
        if (row?.nextLaunchType === "DO_LATER") {
            return (
                <Stack spacing={0.5}>
                    <Chip size="small" color="info" label="Сделать позже" sx={{width: "fit-content"}}/>
                    <Typography variant="caption" color="text.secondary">
                        {fmtMsk(row.nextLaunchAt)}
                        {row.nextLaunchDoLaterId ? ` • ID ${row.nextLaunchDoLaterId}` : ""}
                    </Typography>
                </Stack>
            );
        }

        if (row?.nextLaunchType === "INTERVAL") {
            return (
                <Stack spacing={0.5}>
                    <Chip size="small" color="primary" variant="outlined" label="Интервал" sx={{width: "fit-content"}}/>
                    <Typography variant="caption" color="text.secondary">
                        Каждые {row.nextLaunchIntervalMinutes ?? row.intervalMinutes} мин
                    </Typography>
                </Stack>
            );
        }

        return <Typography variant="body2" color="text.secondary">—</Typography>;
    };

    const handleDelete = async (e, id, name) => {
        e?.stopPropagation?.();
        if (!window.confirm(`Удалить отложенное действие${name ? ` "${name}"` : ""}?\nЛоги и параметры будут удалены. Скрипт будет удален, если больше нигде не используется.`)) {
            return;
        }
        try {
            await deleteDeferredAction(id);
            await handleRefresh();
        } catch (err) {
            console.error(err);
            alert("❌ Не удалось удалить");
        }
    };

    return (
        <Box p={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Отложенные действия
                </Typography>

                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        startIcon={<ScheduleIcon />}
                        onClick={handleCreateDeferredAction}
                        sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 3,
                            py: 1.2,
                            boxShadow: 1,
                            "&:hover": { boxShadow: 3 }
                        }}
                    >
                        Создать отложенное действие
                    </Button>

                    <Tooltip title="Обновить список">
                        <IconButton onClick={handleRefresh} sx={{ ml: 1 }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {loading && (
                <Stack alignItems="center" py={6}>
                    <CircularProgress />
                    <Typography variant="body2" mt={2} color="text.secondary">
                        Загружаем отложенные действия…
                    </Typography>
                </Stack>
            )}

            {!loading && err && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {err}
                </Alert>
            )}

            <Paper sx={{p: 2, mb: 2}}>
                <Stack spacing={0.5} sx={{mb: 1.5}}>
                    <Typography variant="subtitle1">Тренд ошибок по запускам</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Мультилинейный график: X = runCreatedAt, Y = количество событий, линии = SUCCESS + errorType.
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 1}}>
                    <Typography variant="caption" color="text.secondary">
                        Фильтр по типам действий:
                    </Typography>
                    {ACTION_TYPE_FILTERS.map((f) => {
                        const selected = includedActionTypes.includes(f.value);
                        return (
                            <Chip
                                key={f.value}
                                size="small"
                                variant={selected ? "filled" : "outlined"}
                                color={selected ? "primary" : "default"}
                                label={f.label}
                                onClick={() => toggleActionTypeFilter(f.value)}
                                sx={{cursor: "pointer"}}
                            />
                        );
                    })}
                    {includedActionTypes.length > 0 && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setIncludedActionTypes([])}
                            sx={{textTransform: "none", minWidth: "auto", px: 0.5}}
                        >
                            Сбросить
                        </Button>
                    )}
                    <FormControlLabel
                        sx={{ml: {xs: 0, sm: 1}}}
                        control={
                            <Switch
                                size="small"
                                checked={excludeMixedActionTypes}
                                onChange={(e) => setExcludeMixedActionTypes(e.target.checked)}
                            />
                        }
                        label={
                            <Typography variant="caption" color="text.secondary">
                                Исключать смешанные типы
                            </Typography>
                        }
                    />
                </Stack>

                {trendLoading ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{py: 1}}>
                        <CircularProgress size={18}/>
                        <Typography variant="body2" color="text.secondary">Загружаем график…</Typography>
                    </Stack>
                ) : trendErr ? (
                    <Alert severity="warning">{trendErr}</Alert>
                ) : trendPoints.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {(includedActionTypes.length > 0 || excludeMixedActionTypes)
                            ? "Нет данных по выбранному фильтру."
                            : "Нет запусков для построения графика."}
                    </Typography>
                ) : (
                    <ErrorTypeTrendChart
                        points={trendPoints}
                        onNavigateToAction={(deferredActionId) =>
                            navigate(`/deferred-actions/details?id=${deferredActionId}`)
                        }
                    />
                )}
            </Paper>

            {!loading && !err && rows?.length === 0 && (
                <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Пока нет отложенных действий
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Нажмите «Создать отложенное действие», чтобы добавить первое.
                    </Typography>
                </Paper>
            )}

            {!loading && !err && rows?.length > 0 && (
                <TableContainer component={Paper}>
                    <Table size="medium" aria-label="deferred-actions">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 110 }}>ID</TableCell>
                                <TableCell>Название</TableCell>
                                <TableCell sx={{ width: 160 }}>Статус</TableCell>
                                <TableCell sx={{ width: 280 }}>Скоро запустится</TableCell>
                                <TableCell sx={{ width: 220 }}>Создано</TableCell>
                                <TableCell sx={{ width: 60 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover onClick={() => {
                                    navigate('/deferred-actions/details?id=' + r.id);
                                }} style={{ cursor: "pointer" }}>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {r.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="subtitle2">{r.name}</Typography>
                                    </TableCell>
                                    <TableCell>
	                                        <Stack direction="row" spacing={1} alignItems="center">
	                                            {(() => {
	                                                const st = r?.overallStatus || "NOT_STARTED";
	                                                const meta = OVERALL_STATUS[st] || { label: String(st), color: "default", variant: "outlined" };
	                                                return (
	                                                    <Chip
	                                                        size="small"
	                                                        label={meta.label}
	                                                        color={meta.color}
	                                                        variant={meta.variant}
	                                                    />
	                                                );
	                                            })()}

	                                            {(r?.intervalMinutes ?? 0) > 0 ? (
	                                                <Chip
	                                                    size="small"
	                                                    label={r.isEnabled ? "Включено" : "Выключено"}
	                                                    color={r.isEnabled ? "success" : "default"}
	                                                    variant={r.isEnabled ? "filled" : "outlined"}
	                                                />
	                                            ) : null}
	                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {renderNextLaunch(r)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{fmt(r.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="Удалить">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={(e) => handleDelete(e, r.id, r.name)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
